import React from "react";
import Modal from "@mui/material/Modal";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import InstaColors, {
  createSearchBoxStyle,
  fontFamily,
  reviewScreenButtonWithColor,
  reviewScreenButtonWithOutColor,
} from "../Constants/constants";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import { BASE_URL } from "../../App_Settings/app_settings";
import { InputBase } from "@mui/material";
import { getUserIdFromLocalStorage } from "../../API_Endpoints/APIEndpoints";
import {
  COUNTRY_CODES,
  getCountryOptions,
  validateMobileNumber,
  checkCountryCodeRequired,
} from "../../Utility/countryCodesUtil";

export default function UserModalComponent({
  show,
  handleClose,
  onSave,
  handleEdit,
  title,
  isLoading,
  isSuperAdmin: propIsSuperAdmin,
}) {
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [countryCode, setCountryCode] = React.useState("+44");
  const [mobileNumber, setMobileNumber] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [isSystemAdmin, setIsSystemAdmin] = React.useState(false);
  const [isMaker, setIsMaker] = React.useState(false);
  const [isChecker, setIsChecker] = React.useState(false);
  const [isSuspended, setIsSuspended] = React.useState(true);
  const [mfaEnabled, setMfaEnabled] = React.useState(false);
  const [mfaEmail, setMfaEmail] = React.useState(false);
  const [mfaMobile, setMfaMobile] = React.useState(false);
  const [maxConcurrentSessions, setMaxConcurrentSessions] = React.useState(1);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = React.useState([]);
  const [businessUnits, setBusinessUnits] = React.useState([]);
  const [defaultBusinessUnitId, setDefaultBusinessUnitId] = React.useState(null);
  const [roles, setRoles] = React.useState([]);
  const [errors, setErrors] = React.useState({});
  const [organizations, setOrganizations] = React.useState([]);
  const [selectedOrganization, setSelectedOrganization] = React.useState(null);
  // Use the prop instead of local state
  const isSuperAdmin = propIsSuperAdmin || false;
  const [organizationSearchValue, setOrganizationSearchValue] = React.useState("");

  // Password Validation
  const passwordRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

  const validateFields = () => {
    let fieldErrors = {};

    // First Name validation
    if (!firstName || !firstName.trim()) {
      fieldErrors.firstName = "First Name is required.";
    }

    // Last Name validation
    if (!lastName || !lastName.trim()) {
      fieldErrors.lastName = "Last Name is required.";
    }

    // Email validation
    if (!email || !email.trim()) {
      fieldErrors.email = "Email is required.";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        fieldErrors.email = "Please enter a valid email address.";
      }
    }

    // Mobile Number and Country Code validation

    if (!mobileNumber || !mobileNumber.trim()) {
      fieldErrors.mobileNumber = "Mobile Number is required.";
    } else {
      const countryCodeError = checkCountryCodeRequired(countryCode);

      if (countryCodeError && typeof countryCodeError === "object" && countryCodeError.hasError) {
        fieldErrors.mobileNumber = countryCodeError.errorMessage || "Country code is required.";
      } else if (countryCodeError && typeof countryCodeError === "string") {
        fieldErrors.mobileNumber = countryCodeError;
      } else if (
        !countryCodeError ||
        (typeof countryCodeError === "object" && !countryCodeError.hasError)
      ) {
        const mobileError = validateMobileNumber(mobileNumber, countryCode);

        if (mobileError && !mobileError.isValid) {
          fieldErrors.mobileNumber = mobileError.message;
        }
      }
    }

    // Password validation (only for new users)
    if (!handleEdit) {
      if (!password || !password.trim()) {
        fieldErrors.password = "Password is required.";
      } else if (!passwordRegex.test(password)) {
        fieldErrors.password =
          "Password must be 8+ characters with an uppercase letter, a number, and a special character.";
      }
    }

    // Business Units validation

    if (selectedBusinessUnits.length === 0) {
      fieldErrors.businessUnits = "At least one Client must be selected.";
    }

    // Default Client validation

    if (selectedBusinessUnits.length > 0 && !defaultBusinessUnitId) {
      fieldErrors.defaultBusinessUnit = "Please select a default client from the selected clients.";
    }

    // Organization validation for super admin

    if (isSuperAdmin && !selectedOrganization) {
      fieldErrors.organization = "Please select an organization.";
    }

    // Roles validation

    if (!isSystemAdmin && !isMaker && !isChecker) {
      fieldErrors.roles = "At least one Role must be selected.";
    }

    setErrors(fieldErrors);

    const isValid = Object.keys(fieldErrors).length === 0;

    return isValid;
  };

  const handleSaveClick = () => {
    // Always run validation to show errors
    const isValid = validateFields();

    if (!isValid) {
      console.log("❌ Validation failed, showing errors");
      return;
    }

    const assignedRoles = [];
    roles.forEach((role) => {
      if (
        (role.name === "Admin" && isSystemAdmin) ||
        (role.name === "Maker" && isMaker) ||
        (role.name === "Checker" && isChecker)
      ) {
        assignedRoles.push({ roleId: role.id });
      }
    });

    const activeValue = isSuspended;

    // Concatenate country code with mobile number if both exist
    const fullMobileNumber =
      mobileNumber && mobileNumber.trim()
        ? countryCode
          ? `${countryCode}${mobileNumber}`
          : mobileNumber
        : mobileNumber;

    const newRow = {
      firstName,
      lastName,
      email,
      mobileNumber: fullMobileNumber,
      password: password,
      activeValue,
      mfaEnabled,
      mfaEmail,
      mfaMobile,
      maxConcurrentSessions: parseInt(maxConcurrentSessions) || 1,
      roles: assignedRoles,
      businessUnits: selectedBusinessUnits.map((unit) => ({
        businessUnitId: unit.id,
        isDefault: unit.id === defaultBusinessUnitId,
      })),
      organizationId: selectedOrganization ? selectedOrganization.id : undefined,
      // Include organization object for UI consistency
      organization: selectedOrganization,
    };

    handleClose();
    onSave(newRow);
  };

  React.useEffect(() => {
    const initializeData = async () => {
      try {
        // Use the prop value for super admin check
        const isSuperAdminUser = isSuperAdmin;

        // Fetch roles
        const rolesResponse = await fetch(`${BASE_URL}/RolePermission/roles`, {
          headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
        });
        const rolesData = await rolesResponse.json();
        setRoles(rolesData);

        // Fetch organizations if super admin
        if (isSuperAdminUser) {
          const userDataJSON = localStorage.getItem("token");
          if (userDataJSON) {
            const userData = JSON.parse(userDataJSON);
            const orgResponse = await fetch(
              `${BASE_URL}/Organization/${getUserIdFromLocalStorage().userId}/${getUserIdFromLocalStorage().organizationId}`,
              {
                headers: { Authorization: `Bearer ${getUserIdFromLocalStorage().token}` },
              }
            );
            const orgData = await orgResponse.json();
            // Ensure all organizations have valid IDs and names
            const validOrgData = orgData.filter((org) => org && org.id && org.name);

            setOrganizations(validOrgData.reverse()); // Reverse to show newest first
          }
        }

        // Fetch business units - for regular admin only (super admin will fetch when org is selected)
        if (!isSuperAdminUser) {
          const userDataJSON = localStorage.getItem("token");
          if (userDataJSON) {
            const { organizationId } = JSON.parse(userDataJSON);
            const { getBusinessUnits } = await import("../../Utility/utility");
            const buData = await getBusinessUnits(organizationId);
            setBusinessUnits(buData.filter((row) => row.isDeleted === false));
          }
        }
      } catch (error) {
        console.error("Error initializing data:", error);
      }
    };

    initializeData();
  }, [isSuperAdmin]);

  React.useEffect(() => {
    if (handleEdit) {
      setFirstName(handleEdit.firstName || "");
      setLastName(handleEdit.lastName || "");
      setEmail(handleEdit.email || "");

      // Parse mobile number to extract country code and number
      const fullMobileNumber = handleEdit.mobileNumber || "";
      if (fullMobileNumber && fullMobileNumber.startsWith("+")) {
        // Find matching country code from COUNTRY_CODES
        let foundCode = null;
        let numberPart = fullMobileNumber;

        // Sort by dial code length (longest first) to match longest codes first
        const sortedCodes = [...COUNTRY_CODES].sort(
          (a, b) => b.dialCode.length - a.dialCode.length
        );

        for (const country of sortedCodes) {
          if (fullMobileNumber.startsWith(country.dialCode)) {
            foundCode = country.dialCode;
            numberPart = fullMobileNumber.substring(country.dialCode.length);
            break;
          }
        }

        setCountryCode(foundCode || "+44");
        setMobileNumber(numberPart);
      } else {
        setMobileNumber(fullMobileNumber);
        setCountryCode("+44"); // Default to UK
      }

      setIsSuspended(handleEdit.isSuspended == false ? true : false);
      setMfaEnabled(handleEdit.mfaEnabled || false);
      setMfaEmail(handleEdit.mfaEmail || false);
      setMfaMobile(handleEdit.mfaMobile || false);
      setMaxConcurrentSessions(handleEdit.maxConcurrentSessions || 1);
      setIsSystemAdmin(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Admin")
        )
      );
      setIsMaker(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Maker")
        )
      );
      setIsChecker(
        handleEdit.userRoles?.some((role) =>
          roles.find((r) => r.id === role.id && r.name === "Checker")
        )
      );
      setSelectedBusinessUnits(
        businessUnits.filter((unit) =>
          handleEdit.businessUnits?.some((editUnit) => editUnit.id === unit.id)
        )
      );

      // Set default business unit when editing
      const defaultUnit = handleEdit.businessUnits?.find((unit) => unit.isDefault);
      if (defaultUnit) {
        setDefaultBusinessUnitId(defaultUnit.id);
      }

      // Handle organization selection when editing a user
      if (isSuperAdmin) {
        if (handleEdit.organizationId && organizations.length > 0) {
          // Find the organization in the list by ID
          const foundOrg = organizations.find((org) => org.id === handleEdit.organizationId);
          if (foundOrg) {
            setSelectedOrganization(foundOrg);
            // Fetch business units for this organization
            fetchBusinessUnitsForOrganization(foundOrg.id);
          }
        }
      }
    } else {
      setFirstName("");
      setLastName("");
      setEmail("");
      setMobileNumber("");
      setCountryCode("+44"); // Reset to default country code
      setPassword(""); // Clear password field
      setIsSystemAdmin(false);
      setIsMaker(false);
      setIsChecker(false);
      setMfaEnabled(false);
      setMfaEmail(false);
      setMfaMobile(false);
      setMaxConcurrentSessions(1);
      setSelectedBusinessUnits([]);
      setSelectedOrganization(null);
      setDefaultBusinessUnitId(null);
    }
  }, [handleEdit, isSuperAdmin, organizations]);

  const disableOtherRoles = (role) => {
    if (role === "Admin") {
      setIsMaker(false);
      setIsChecker(false);
    } else {
      setIsSystemAdmin(false);
    }
  };

  const handleDefaultBusinessUnitChange = (businessUnitId) => {
    setDefaultBusinessUnitId(businessUnitId);
  };

  // Fetch business units for selected organization (super admin only)
  const fetchBusinessUnitsForOrganization = async (organizationId) => {
    try {
      const { getBusinessUnits } = await import("../../Utility/utility");
      const buData = await getBusinessUnits(organizationId);
      const filteredBusinessUnits = buData.filter((row) => row.isDeleted === false);
      setBusinessUnits(filteredBusinessUnits);

      // Reset selected business units and default when organization changes
      setSelectedBusinessUnits([]);
      setDefaultBusinessUnitId(null);
    } catch (error) {
      console.error("Error fetching business units for organization:", error);
      setBusinessUnits([]);
      setSelectedBusinessUnits([]);
      setDefaultBusinessUnitId(null);
    }
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return (
      firstName &&
      firstName.trim() &&
      lastName &&
      lastName.trim() &&
      email &&
      email.trim() &&
      mobileNumber &&
      mobileNumber.trim() &&
      (handleEdit || (password && passwordRegex.test(password))) &&
      selectedBusinessUnits.length > 0 &&
      defaultBusinessUnitId &&
      (isSystemAdmin || isMaker || isChecker) &&
      (isSuperAdmin ? selectedOrganization : true)
    ); // Super admin must select organization
  };

  const renderRequired = (fieldName) => (
    <span
      style={{ color: "black", display: "inline-block", width: "130px", fontFamily: fontFamily }}
    >
      {fieldName}
      <span style={{ color: "red" }}>*</span>{" "}
    </span>
  );

  return (
    <Modal open={show} onClose={handleClose} aria-labelledby="add-user-modal">
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "600px",
          maxHeight: "90vh",
          padding: "20px",
          outline: "none",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <h6 style={{ color: "#2aa7e0", fontFamily: fontFamily }}>{title}</h6>
        <div
          style={{
            height: "1px",
            width: "100%",
            backgroundColor: "#2aa7e0",
            marginBottom: "20px",
            marginRight: "10px",
          }}
        ></div>

        <div>
          <div style={{ marginBottom: 10 }}>
            {renderRequired("First Name")}
            <InputBase
              style={{
                ...createSearchBoxStyle({ width: "50ch" }),
                border: errors.firstName ? "1px solid #f44336" : undefined,
              }}
              inputProps={{ "aria-label": "Entity Search", style: { padding: "0 8px" } }}
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                if (errors.firstName && e.target.value.trim()) {
                  setErrors({ ...errors, firstName: "" });
                }
              }}
            />
            {errors.firstName && (
              <div
                style={{
                  color: "#f44336",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginLeft: "140px",
                }}
              >
                {errors.firstName}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            {renderRequired("Last Name")}
            <InputBase
              style={{
                ...createSearchBoxStyle({ width: "50ch" }),
                border: errors.lastName ? "1px solid #f44336" : undefined,
              }}
              inputProps={{ "aria-label": "Entity Search", style: { padding: "0 8px" } }}
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                if (errors.lastName && e.target.value.trim()) {
                  setErrors({ ...errors, lastName: "" });
                }
              }}
            />
            {errors.lastName && (
              <div
                style={{
                  color: "#f44336",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginLeft: "140px",
                }}
              >
                {errors.lastName}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 10 }}>
            {renderRequired("Email")}
            <InputBase
              style={{
                ...createSearchBoxStyle({ width: "50ch" }),
                border: errors.email ? "1px solid #f44336" : undefined,
              }}
              inputProps={{ "aria-label": "Entity Search", style: { padding: "0 8px" } }}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (errors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)) {
                  setErrors({ ...errors, email: "" });
                }
              }}
            />
            {errors.email && (
              <div
                style={{
                  color: "#f44336",
                  fontSize: "12px",
                  marginTop: "4px",
                  marginLeft: "140px",
                }}
              >
                {errors.email}
              </div>
            )}
          </div>
          <div style={{ marginBottom: 10, display: "flex", flexDirection: "row" }}>
            <div style={{ width: 130 }}>{renderRequired("Mobile Number")}</div>
            <div style={{ display: "flex", flexDirection: "row", gap: "10px" }}>
              <Autocomplete
                options={getCountryOptions()}
                value={getCountryOptions().find((opt) => opt.value === countryCode) || null}
                onChange={(event, newValue) => {
                  setCountryCode(newValue ? newValue.value : "");
                }}
                getOptionLabel={(option) => option.value || ""}
                renderOption={(props, option) => (
                  <li {...props} style={{ fontFamily: fontFamily, fontSize: 14 }}>
                    {option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select country code"
                    variant="standard"
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      style: {
                        width: "125px",
                        height: "36px",
                        backgroundColor: "#EAF6FC",
                        borderRadius: "15px",
                        fontSize: "14px",
                        border: "none",
                        outline: "none",
                        marginLeft: "10px",
                        fontFamily: fontFamily,
                        paddingLeft: "10px",
                      },
                    }}
                  />
                )}
                ListboxProps={{
                  style: {
                    maxHeight: 300,

                    fontFamily: fontFamily,
                  },
                }}
                noOptionsText="No countries found"
                isOptionEqualToValue={(option, value) => option.value === value.value}
                style={{ width: "15ch" }}
              />

              <InputBase
                style={{
                  ...createSearchBoxStyle({ width: "220px" }),
                  border: errors.mobileNumber ? "1px solid #f44336" : undefined,
                }}
                inputProps={{
                  "aria-label": "Mobile Number",
                  style: { padding: "0 8px" },
                  maxLength: 15,
                  width: "220px",
                }}
                placeholder="Enter mobile number"
                value={mobileNumber}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  setMobileNumber(value);
                  if (errors.mobileNumber && value.trim()) {
                    setErrors({ ...errors, mobileNumber: "" });
                  }
                }}
              />
            </div>
          </div>
          {errors.mobileNumber && (
            <div
              style={{
                color: "#f44336",
                fontSize: "12px",
                marginTop: "4px",
                marginLeft: "140px",
              }}
            >
              {errors.mobileNumber}
            </div>
          )}
          {!handleEdit && (
            <div style={{ marginBottom: 10 }}>
              {renderRequired("Password")}
              <InputBase
                style={{
                  ...createSearchBoxStyle({ width: "50ch" }),
                  border: errors.password ? "1px solid #f44336" : undefined,
                }}
                inputProps={{
                  "aria-label": "Entity Search",
                  style: { padding: "0 8px" },
                  autoComplete: "new-password",
                }}
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  // Clear error if password meets all requirements
                  if (errors.password) {
                    const pwd = e.target.value;
                    const hasMinLength = pwd.length >= 8;
                    const hasUpperCase = /[A-Z]/.test(pwd);
                    const hasNumber = /[0-9]/.test(pwd);
                    const hasSpecialChar = /[!@#$%^&*]/.test(pwd);
                    if (hasMinLength && hasUpperCase && hasNumber && hasSpecialChar) {
                      setErrors({ ...errors, password: "" });
                    }
                  }
                }}
                name="new-password"
                autoComplete="new-password"
              />
              {/* Password Requirements (show only after typing and when not all satisfied) */}
              {password.length > 0 &&
                !(
                  password.length >= 8 &&
                  /[A-Z]/.test(password) &&
                  /[0-9]/.test(password) &&
                  /[!@#$%^&*]/.test(password)
                ) && (
                  <div
                    style={{
                      marginLeft: "130px",
                      marginTop: "5px",
                      fontSize: "12px",
                      color: "#666",
                    }}
                  >
                    <div style={{ marginBottom: "2px" }}>Password must contain:</div>
                    <div style={{ color: password.length >= 8 ? "#4caf50" : "#f44336" }}>
                      ✓ At least 8 characters {password.length >= 8 ? "✓" : "✗"}
                    </div>
                    <div style={{ color: /[A-Z]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One uppercase letter {/[A-Z]/.test(password) ? "✓" : "✗"}
                    </div>
                    <div style={{ color: /[0-9]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One number {/[0-9]/.test(password) ? "✓" : "✗"}
                    </div>
                    <div style={{ color: /[!@#$%^&*]/.test(password) ? "#4caf50" : "#f44336" }}>
                      ✓ One special character (!@#$%^&*) {/[!@#$%^&*]/.test(password) ? "✓" : "✗"}
                    </div>
                  </div>
                )}
              {errors.password && (
                <div
                  style={{
                    color: "#f44336",
                    fontSize: "12px",
                    marginTop: "4px",
                    marginLeft: "140px",
                  }}
                >
                  {errors.password}
                </div>
              )}
            </div>
          )}

          {isSuperAdmin && (
            <div style={{ marginBottom: 10, display: "flex", flexDirection: "row" }}>
              {renderRequired("Organization")}
              <div style={{ width: "50ch" }}>
                <Autocomplete
                  options={organizations}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedOrganization}
                  onChange={(event, newValue) => {
                    setSelectedOrganization(newValue);
                    // Fetch business units for the selected organization
                    if (newValue && newValue.id) {
                      fetchBusinessUnitsForOrganization(newValue.id);
                    } else {
                      // Clear business units if no organization selected
                      setBusinessUnits([]);
                      setSelectedBusinessUnits([]);
                      setDefaultBusinessUnitId(null);
                    }
                  }}
                  inputValue={organizationSearchValue}
                  onInputChange={(event, newInputValue) => {
                    setOrganizationSearchValue(newInputValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      placeholder="Search organization..."
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                        style: {
                          width: "360px",
                          height: "36px",
                          backgroundColor: "#EAF6FC",
                          borderRadius: "15px",
                          fontSize: "14px",
                          border: "none",
                          outline: "none",
                          marginLeft: "10px",
                          fontFamily: fontFamily,
                          paddingLeft: "10px",
                        },
                      }}
                      // fullWidth
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  noOptionsText="No organizations found"
                  fullWidth
                />
              </div>
            </div>
          )}
          {errors.organization && (
            <div
              style={{
                color: "#f44336",
                fontSize: "12px",
                marginTop: "4px",
                marginLeft: "140px",
              }}
            >
              {errors.organization}
            </div>
          )}

          <div style={{ marginBottom: 10, display: "flex", flexDirection: "row" }}>
            {renderRequired("Roles")}
            <div>
              <Checkbox
                style={{ color: InstaColors.primary, marginLeft: "10px" }}
                checked={isSystemAdmin}
                onChange={(e) => {
                  setIsSystemAdmin(e.target.checked);
                  disableOtherRoles("Admin");
                  if (errors.roles && e.target.checked) {
                    setErrors({ ...errors, roles: "" });
                  }
                }}
                disabled={isMaker || isChecker}
              />
              <span style={{ marginLeft: 4 }}>Admin</span>
            </div>
            <div>
              <Checkbox
                style={{ color: InstaColors.primary, marginLeft: "10px" }}
                checked={isChecker}
                onChange={(e) => {
                  setIsChecker(e.target.checked);
                  disableOtherRoles("Checker");
                  if (errors.roles && e.target.checked) {
                    setErrors({ ...errors, roles: "" });
                  }
                }}
                disabled={isSystemAdmin}
              />
              <span style={{ marginLeft: 4 }}>Approver</span>
            </div>
            <div>
              <Checkbox
                style={{ color: InstaColors.primary, marginLeft: "10px" }}
                checked={isMaker}
                onChange={(e) => {
                  setIsMaker(e.target.checked);
                  disableOtherRoles("Maker");
                  if (errors.roles && e.target.checked) {
                    setErrors({ ...errors, roles: "" });
                  }
                }}
                disabled={isSystemAdmin}
              />
              <span style={{ marginLeft: 4 }}>Maker</span>
            </div>
          </div>
          {errors.roles && (
            <div
              style={{
                color: "#f44336",
                fontSize: "12px",
                marginTop: "4px",
                marginLeft: "140px",
              }}
            >
              {errors.roles}
            </div>
          )}

          <div style={{ marginBottom: 10, display: "flex", flexDirection: "row" }}>
            <div style={{ width: 130 }}>{renderRequired("Clients")}</div>

            <div style={{ width: "100%" }}>
              <Autocomplete
                multiple
                options={businessUnits}
                getOptionLabel={(option) => option.name || ""}
                value={selectedBusinessUnits}
                onChange={(e, value) => {
                  setSelectedBusinessUnits(value);
                  if (errors.businessUnits && value.length > 0) {
                    setErrors({ ...errors, businessUnits: "" });
                  }
                  // Reset default if the selected default is no longer in the list
                  if (
                    defaultBusinessUnitId &&
                    !value.find((unit) => unit.id === defaultBusinessUnitId)
                  ) {
                    setDefaultBusinessUnitId(null);
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip key={index} {...getTagProps({ index })} label={option.name} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    // fullWidth
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      style: {
                        width: "360px",
                        height: "44px",
                        backgroundColor: "#EAF6FC",
                        borderRadius: "15px",
                        fontSize: "14px",
                        border: errors.businessUnits ? "1px solid #f44336" : "none",
                        outline: "none",
                        marginLeft: "10px",
                        fontFamily: fontFamily,
                        paddingLeft: "10px",
                      },
                    }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            </div>
          </div>
          {errors.businessUnits && (
            <div
              style={{
                color: "#f44336",
                fontSize: "12px",
                marginTop: "4px",
                marginLeft: "140px",
              }}
            >
              {errors.businessUnits}
            </div>
          )}

          {selectedBusinessUnits.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {renderRequired("Default Client")}
              <div style={{ marginLeft: 0, marginTop: 5 }}>
                {selectedBusinessUnits.map((unit) => (
                  <div
                    key={unit.id}
                    style={{ display: "flex", alignItems: "center", marginBottom: 5 }}
                  >
                    <input
                      type="radio"
                      id={`default-${unit.id}`}
                      name="defaultBusinessUnit"
                      value={unit.id}
                      checked={defaultBusinessUnitId === unit.id}
                      onChange={() => {
                        handleDefaultBusinessUnitChange(unit.id);
                        if (errors.defaultBusinessUnit) {
                          setErrors({ ...errors, defaultBusinessUnit: "" });
                        }
                      }}
                      style={{ marginRight: 8 }}
                    />
                    <label
                      htmlFor={`default-${unit.id}`}
                      style={{ fontFamily: fontFamily, cursor: "pointer" }}
                    >
                      {unit.name}
                    </label>
                  </div>
                ))}
                {errors.defaultBusinessUnit && (
                  <div
                    style={{
                      color: "#f44336",
                      fontSize: "12px",
                      marginTop: 4,
                      marginLeft: "10px",
                    }}
                  >
                    {errors.defaultBusinessUnit}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* MFA Settings */}
          <div style={{ marginTop: 10, display: "flex", flexDirection: "row" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                fontWeight: 500,
                fontSize: "14px",
                color: "#333",
              }}
            >
              Multi-Factor Authentication (MFA):
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  style={{ color: InstaColors.primary }}
                  checked={mfaEmail}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setMfaEmail(newValue);
                    // Auto-enable MFA if either checkbox is checked
                    if (newValue || mfaMobile) {
                      setMfaEnabled(true);
                    } else {
                      setMfaEnabled(false);
                    }
                  }}
                />
                <span style={{ marginLeft: 4, fontSize: "14px" }}>Email</span>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  style={{ color: InstaColors.primary }}
                  checked={mfaMobile}
                  onChange={(e) => {
                    const newValue = e.target.checked;
                    setMfaMobile(newValue);
                    // Auto-enable MFA if either checkbox is checked
                    if (newValue || mfaEmail) {
                      setMfaEnabled(true);
                    } else {
                      setMfaEnabled(false);
                    }
                  }}
                />
                <span style={{ marginLeft: 4, fontSize: "14px" }}>Mobile</span>
              </div>
            </div>
          </div>
          {/* <div style={{ fontSize: '12px', color: '#666',  }}>
                {mfaEnabled ? '✓ MFA Enabled' : 'Select at least one option to enable MFA'}
              </div> */}

          {/* Max Concurrent Sessions */}
          {/* <div
            style={{ marginTop: 10, display: "flex", flexDirection: "row", alignItems: "center" }}
          >
            <div
              style={{
                fontWeight: 500,
                fontSize: "14px",
                color: "#333",
                width: 200,
              }}
            >
              Max Concurrent Sessions:
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <InputBase
                type="number"
                value={maxConcurrentSessions}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= 10) {
                    setMaxConcurrentSessions(val);
                  }
                }}
                inputProps={{
                  min: 1,
                  max: 10,
                  style: {
                    textAlign: "center",
                    width: "60px",
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    fontSize: "14px",
                  },
                }}
              />
           
            </div>
          </div> */}

          <div style={{ marginTop: 10 }}>
            <Checkbox
              style={{ color: InstaColors.primary }}
              checked={isSuspended}
              onChange={(e) => {
                setIsSuspended(e.target.checked);
              }}
            />
            <span style={{ marginLeft: 4 }}>Active</span>
          </div>

          <div
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "10px",
              marginTop: 20,
              marginBottom: 10,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            <div style={{ marginLeft: "auto" }}></div>
            <Button
              variant="contained"
              style={reviewScreenButtonWithOutColor(100)}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              style={{
                ...reviewScreenButtonWithColor(100),
              }}
              onClick={handleSaveClick}
            >
              Save
            </Button>
            <div style={{ marginRight: "auto" }}></div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
